import { useMemo } from 'react'
import { useQuery } from '@tanstack/react-query'
import { useClobClient } from '@/hooks/useClobClient'
import type { OrderInfo } from '@/lib/rangeMarketUtils'

function parseSize(s: string): number {
  const n = parseFloat(s)
  return Number.isFinite(n) ? n : 0
}

/**
 * CLOB: open orders for the active wallet, keyed by asset_id (token id).
 * Aggregates multiple orders per token: sum size_matched, sum original_size, first order price.
 */
export function useOpenOrders(enabled: boolean = true): Map<string, OrderInfo> {
  const clobClient = useClobClient()
  const { data: orders } = useQuery({
    queryKey: ['openOrders', !!clobClient],
    queryFn: async () => (clobClient ? await clobClient.getOpenOrders() : []),
    enabled: !!clobClient && enabled,
    staleTime: 5_000,
    refetchInterval: 5_000,
  })
  return useMemo(() => {
    const map = new Map<string, OrderInfo>()
    if (!orders?.length) return map
    for (const o of orders) {
      const aid = o.asset_id
      if (!aid) continue
      const price = parseSize(o.price)
      const matched = parseSize(o.size_matched)
      const total = parseSize(o.original_size)
      const existing = map.get(aid)
      if (existing) {
        map.set(aid, {
          orderPrice: existing.orderPrice,
          sizeMatched: existing.sizeMatched + matched,
          sizeTotal: existing.sizeTotal + total,
        })
      } else {
        map.set(aid, { orderPrice: price, sizeMatched: matched, sizeTotal: total })
      }
    }
    return map
  }, [orders])
}
